import React, { FC, useRef } from "react"

import { useTranslation } from "react-i18next"

import { SingleOptTimeRangePicker } from "components/date/SingleOptTimePeriodPicker"

import { balanceService } from "services/balance/balance.service"
import { BalanceTxType } from "services/balance/type"

const btnClass =
  "h-10 px-4 rounded-lg text-sm font-medium border border-[rgba(132,0,168,1)] text-[rgba(132,0,168,1)] hover:bg-[rgba(132,0,168,0.1)] transition-all dark:text-white dark:border-[rgba(132,0,168,1)] dark:hover:bg-[rgba(132,0,168,0.2)]"

interface BalanceToolbarProps {
  txType: BalanceTxType
  timeframe: (Date | null)[]
  onTimeframeChange: (dates: (Date | null)[]) => void
  onReloadNeeded: () => void
  onResetAll: () => void
  onOpenReport: () => void
  isAdmin: boolean
}

export const BalanceToolbar: FC<BalanceToolbarProps> = ({
  txType,
  timeframe,
  onTimeframeChange,
  onReloadNeeded,
  onResetAll,
  onOpenReport,
  isAdmin
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const importRef = useRef<HTMLInputElement>(null)

  const handleExportCsv = async () => {
    await balanceService.exportFeesCsv(txType)
  }

  const handleImportCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    await balanceService.importFeesCsv(file)
    if (importRef.current) importRef.current.value = ""
    onReloadNeeded()
  }

  const handleExportTransactions = async () => {
    const start = timeframe?.[0] ?? null
    const end = timeframe?.[1] ?? null
    await balanceService.exportTransactionsCsv({
      tx_type: txType,
      start: start ? start.toISOString() : undefined,
      end: end ? end.toISOString() : undefined
    })
  }

  return (
    <div className="flex flex-row gap-4 items-end flex-wrap">
      <SingleOptTimeRangePicker onTimeframeChange={onTimeframeChange} noLabel />
      <button onClick={handleExportCsv} className={btnClass}>
        {t("balance.exportFees")}
      </button>
      <label className={`${btnClass} flex items-center cursor-pointer`}>
        {t("balance.importFees")}
        <input ref={importRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleImportCsv} />
      </label>
      <button onClick={handleExportTransactions} className={btnClass}>
        {t("balance.exportTransactions")}
      </button>

      <div className="ml-auto flex flex-row gap-4">
        <button onClick={onOpenReport} className={btnClass}>
          {t("balance.settlementReport")}
        </button>
        {isAdmin && (
          <button
            onClick={onResetAll}
            className="h-10 px-4 rounded-lg text-sm font-medium border border-rose-500 text-rose-500 hover:bg-rose-50 transition-all dark:text-rose-400 dark:border-rose-400 dark:hover:bg-rose-900/20"
          >
            {t("balance.resetAvailableAll")}
          </button>
        )}
      </div>
    </div>
  )
}
